import { MouseEvent } from 'react';
import { Control, Controller } from 'react-hook-form';
import { omit, isEqual } from 'lodash-es';
import { CoreBox, CoreFormControlLabel, CoreFormGroup, CoreRadio } from '@youscience/core';

import { RadioOption } from '@interfaces';

import useSchoolSearchFiltersStore from '@store/SchoolSearchFiltersStore';
import useUserPreferencesStore from '@store/UserPreferencesStore';

import { sxStyles } from './RadioButtonGrop.styles';

interface RadioButtonGroupProps {
  name: string;
  options: RadioOption[];
  // eslint-disable-next-line
  control: Control<any>;
  row?: boolean;
  disabled?: boolean;
  onRadioClick?: (filterId: string) => void;
  endAdornment?: JSX.Element;
  onEndAdornmentClick?: (id: string) => void;
}

export function RadioButtonGroup(props: RadioButtonGroupProps) {
  const { name, control, options = [], row, disabled, endAdornment, onEndAdornmentClick, onRadioClick } = props;
  const schoolSearchFilters = useSchoolSearchFiltersStore((state) => state.filters);
  const userPreferences = useUserPreferencesStore((state) => state.userPreferences);

  const savedFilters = userPreferences.filters?.colleges ?? [];

  const handleEndAdornmentClick = (filterId: string) => (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();

    if (onEndAdornmentClick) {
      onEndAdornmentClick(filterId);
    }
  };

  const handleRadioClick = (filterId: string) => () => {
    if (onRadioClick) {
      onRadioClick(filterId);
    }
  };

  return (
    <CoreFormGroup row={row}>
      <Controller
        name={name}
        control={control}
        defaultValue=""
        render={({ field }) => {
          const { ref, ...rest } = field;

          return (
            <>
              {options.map((option, index) => {
                const isChecked = option.value === field.value;

                // TODO replace this logic to parent
                const isEqualStates = isEqual(
                  omit(
                    savedFilters.find((filterItem) => filterItem?.id?.toString() === option.id),
                    'id',
                    'createdDate',
                  ),
                  omit(schoolSearchFilters, 'skip', 'limit'),
                );

                const isCheckRadio = isEqualStates || isChecked;
                const handleClick = isCheckRadio ? undefined : handleRadioClick(option.id as string);

                return (
                  <CoreFormControlLabel
                    sx={sxStyles.radioLabel}
                    key={`${option.value}${index}`}
                    label={option.label}
                    inputRef={ref}
                    disabled={disabled}
                    control={
                      <>
                        <CoreRadio
                          {...rest}
                          ariaLabel={option.label}
                          checked={isCheckRadio}
                          value={option.value}
                          onClick={handleClick}
                        />

                        {endAdornment && (
                          <CoreBox onClick={handleEndAdornmentClick(option.id as string)}>{endAdornment}</CoreBox>
                        )}
                      </>
                    }
                  />
                );
              })}
            </>
          );
        }}
      />
    </CoreFormGroup>
  );
}
